import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { PhoneCall, ClockCounterClockwise, Sparkle } from "@phosphor-icons/react";
import { fetchCallbackAgenda } from "@/lib/api";

let __agendaCache = { counts: { actionable: 0 }, ts: 0 };
const __agendaListeners = new Set();

export function bumpCallbackAgenda() {
  // Force a refetch on next tick; used after save.
  __agendaCache.ts = 0;
  __agendaListeners.forEach((fn) => fn());
}

function useAgendaCount() {
  const [count, setCount] = useState(__agendaCache.counts.actionable || 0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const now = Date.now();
      if (now - __agendaCache.ts < 15_000) {
        setCount(__agendaCache.counts.actionable || 0);
        return;
      }
      try {
        const data = await fetchCallbackAgenda();
        __agendaCache = { counts: data.counts || { actionable: 0 }, ts: Date.now() };
        if (!cancelled) setCount(data.counts?.actionable || 0);
      } catch (e) {
        // ignore
      }
    };
    load();
    const listener = () => load();
    __agendaListeners.add(listener);
    const t = setInterval(load, 30_000);
    return () => {
      cancelled = true;
      __agendaListeners.delete(listener);
      clearInterval(t);
    };
  }, []);

  return count;
}

export function TopNav() {
  const location = useLocation();
  const count = useAgendaCount();
  const onCallbacks = location.pathname === "/callbacks";
  const onHistory = location.pathname === "/history";

  return (
    <header
      data-testid="top-nav"
      className="sticky top-0 z-20 border-b border-border/70 bg-white/90 backdrop-blur"
    >
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-y-2 gap-x-3 px-4 py-3 md:px-10">
        <Link to="/" className="flex min-w-0 items-center gap-2">
          <div className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-md bg-ink text-white">
            <span className="font-display text-sm font-black tracking-tighter">G</span>
          </div>
          <div className="truncate font-display text-base font-bold tracking-tight text-ink">
            GBP Posts Tracker
          </div>
        </Link>
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
          <Link
            to="/prospecting"
            data-testid="nav-prospecting"
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-sans text-xs font-medium transition-colors ${
              location.pathname === "/prospecting"
                ? "border-ink bg-ink text-white"
                : "border-border bg-white text-ink hover:border-electric hover:text-electric"
            }`}
          >
            <Sparkle size={13} weight="fill" />
            Prospecting
          </Link>
          <Link
            to="/history"
            data-testid="nav-history"
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-sans text-xs font-medium transition-colors ${
              onHistory
                ? "border-ink bg-ink text-white"
                : "border-border bg-white text-ink hover:border-electric hover:text-electric"
            }`}
          >
            <ClockCounterClockwise size={13} weight="bold" />
            History
          </Link>
          <Link
            to="/callbacks"
            data-testid="nav-callbacks"
            className={`relative inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-sans text-xs font-medium transition-colors ${
              onCallbacks
                ? "border-electric bg-electric text-white"
                : "border-border bg-white text-ink hover:border-electric hover:text-electric"
            }`}
          >
            <PhoneCall size={13} weight="bold" />
            Callbacks
            {count > 0 ? (
              <span
                data-testid="callback-badge-count"
                className={`absolute -right-1.5 -top-1.5 grid h-4 min-w-[16px] place-items-center rounded-full px-1 font-mono text-[10px] font-bold text-white ${
                  onCallbacks ? "bg-white text-electric" : "bg-[#C92A2A]"
                }`}
              >
                {count > 99 ? "99+" : count}
              </span>
            ) : null}
          </Link>
          <span className="hidden font-mono text-[11px] uppercase tracking-wider text-muted-foreground sm:inline">
            Google Maps · Local SEO
          </span>
        </div>
      </div>
    </header>
  );
}
